import PropTypes from 'prop-types'
import { useContext } from 'react'
import Badge from 'react-bootstrap/Badge'
import { Link, useNavigate } from 'react-router-dom'
import { CTA_CTE, MERCADO_PAGO } from '../../constants/mediosEnviosPagos.js'
import { ID_PRESUPUESTO_CONFIRMADO } from '../../constants/presupuesto.js'
import { ESTADO_PUBLICADA } from '../../constants/publicaciones.js'
import { formatearFecha } from '../../helpers/dateValidation'
import { esPresupuestoVencido, obtenerEstadoVisual } from '../../helpers/presupuestoEstado.js'
import { useAuth } from '../../hooks/useAuth.js'
import { MediosPagoChips } from '../../ui/widgets/index.js'
import { CarritoContext } from '../context/carritoContext'
import { useDeshabilitaCompra } from '../hooks/deshabilitaCompra.js'
import { useTieneCuentaMPVinculada } from '../hooks/useMercadoPago.js'
import Carousel from '../publicaciones/carousel/Carousel.jsx'
import { DetalleLayout } from '../reutilizables/detalleLayout/DetalleLayout.jsx'
import LinkButton from '../ui/LinkButton'
import styles from './PresupuestoCabecera.module.css'

const PresupuestoCabecera = ({
  presupuesto,
  estado,
  estadoTxt,
  esVendedor,
  abrirAdjunto,
  nombreAdjunto,
  mediosPagoBase,
  avisoLimiteCompra = ''
}) => {
  const {
    idEstado,
    estado: estadoPresupuesto,
    titulo,
    idPublicacion,
    idPresupuesto,
    fechaValidez,
    descripcion,
    cantDiasEntrega,
    anticipo,
    empidVendedor,
    idPublicacionEstado
  } = presupuesto

  const { loginDataContext: { aceptaNoHayDatosFiscales, existeDatosFiscales } = {} } = useAuth()
  const navigate = useNavigate()
  const context = useContext(CarritoContext)
  const { tieneCuentaVinculadaMP } = useTieneCuentaMPVinculada(empidVendedor)
  const { ctaCteBloqueada, deshabilitaCompra } = useDeshabilitaCompra({
    ...presupuesto,
    empid: empidVendedor,
    mediosPagos: mediosPagoBase.filter((medio) => medio.tienePresupuesto)
  })

  const idEstadoActual = estado !== null ? estado : idEstado
  const { color: colorEstado, label: labelEstado } = obtenerEstadoVisual({
    idEstado: idEstadoActual,
    estado: estadoTxt !== null ? estadoTxt : estadoPresupuesto,
    fechaValidez
  })
  const vencido = esPresupuestoVencido(idEstadoActual, fechaValidez)

  const mediosPagoConfirmados = mediosPagoBase
    .filter((medio) => medio.tienePresupuesto)
    .map((medio) => {
      const esMercadoPagoDeshabilitado =
        medio.idMedioPago === MERCADO_PAGO && !tieneCuentaVinculadaMP
      const esCtaCteDeshabilitada = medio.idMedioPago === CTA_CTE && ctaCteBloqueada
      const habilitado = !esMercadoPagoDeshabilitado && !esCtaCteDeshabilitada
      const tooltipoCtaCte =
        medio.idMedioPago === CTA_CTE && ctaCteBloqueada
          ? 'Este presupuesto permite cuenta corriente y tu usuario no la tiene habilitada con el vendedor o no es proveedor propio.'
          : `Condición de pago: ${medio.condicionPago} días`
      const tooltipMP = esMercadoPagoDeshabilitado
        ? 'El vendedor no tiene una cuenta de Mercado Pago vinculada.'
        : medio.descripcion

      return {
        id: medio.idMedioPago,
        label: medio.nombre,
        variant: habilitado ? 'default' : 'warning',
        habilitado,
        tooltip: medio.idMedioPago === MERCADO_PAGO ? tooltipMP : tooltipoCtaCte,
        tooltipSize: 'large'
      }
    })

  const comprarAhora = () => {
    context.setIdPublicacionComprar(0)
    context.setIdPresupuestoComprar(idPresupuesto)
    navigate('/Compras')
  }

  return esVendedor ? (
    <div className={styles.headerDetalle}>
      <div className={styles.headerInfo}>
        <span className={styles.nombrePresupuesto}>Presupuesto Nº {idPresupuesto}</span>
        <p className={styles.infoLinea}>
          <span className={styles.infoLineaLabel}>Publicación:</span>{' '}
          <Link to={`/Publicacion/${idPublicacion}`} className={styles.linkPublicacion}>
            {titulo} (N° {idPublicacion})
          </Link>
        </p>
      </div>
      <Badge bg={colorEstado} className={styles.badgeEstado}>
        {labelEstado}
      </Badge>
    </div>
  ) : (
    <DetalleLayout
      imagen={<Carousel detalle={presupuesto} />}
      titulo={titulo}
      idPublicacion={idPublicacion}
      badge={
        <Badge bg={colorEstado} className={styles.badgeEstado}>
          {labelEstado}
        </Badge>
      }
    >
      <div className={styles.infoComprador}>
        <p className={styles.infoLinea}>
          <span className={styles.infoLineaLabel}>Descripción:</span>{' '}
          {descripcion && descripcion.trim() !== '' ? descripcion : 'Sin descripción.'}
        </p>
        {mediosPagoConfirmados.length > 0 && (
          <MediosPagoChips mediosPagos={mediosPagoConfirmados} />
        )}
        <p className={styles.infoLinea}>
          <span className={styles.infoLineaLabel}>Plazo de entrega:</span>{' '}
          {cantDiasEntrega || cantDiasEntrega === 0
            ? `${cantDiasEntrega} día${Number(cantDiasEntrega) === 1 ? '' : 's'}`
            : '-'}
        </p>
        <p className={styles.infoLinea}>
          <span className={styles.infoLineaLabel}>Validez del presupuesto:</span>{' '}
          {fechaValidez ? formatearFecha(fechaValidez) : '-'}
        </p>
        {Number(anticipo) > 0 && (
          <p className={styles.infoLinea}>
            <span className={styles.infoLineaLabel}>Anticipo:</span> {Number(anticipo)}%
          </p>
        )}
        {nombreAdjunto && nombreAdjunto.trim() !== '' && (
          <p className={styles.infoLinea}>
            <span className={styles.infoLineaLabel}>Adjunto:</span>{' '}
            <span className={styles.archivo} onClick={abrirAdjunto} title="Abrir adjunto">
              {nombreAdjunto}
            </span>
          </p>
        )}
        {!vencido && presupuesto.idEstado === ID_PRESUPUESTO_CONFIRMADO && (
          <div className={styles.accionCompra}>
            <div className={styles.botonComprarWrapper}>
              <button
                type="button"
                className="btn-primary btnAccionCompra"
                onClick={comprarAhora}
                disabled={
                  deshabilitaCompra ||
                  Boolean(avisoLimiteCompra) ||
                  (!aceptaNoHayDatosFiscales && !existeDatosFiscales)
                }
                aria-disabled={deshabilitaCompra || Boolean(avisoLimiteCompra)}
                title={avisoLimiteCompra || undefined}
              >
                Comprar Ahora
              </button>
              {!aceptaNoHayDatosFiscales && !existeDatosFiscales && (
                <span className={styles.tooltipDatosFiscales}>
                  Complete sus datos fiscales para continuar
                </span>
              )}
            </div>
          </div>
        )}
        {vencido && (
          <div className={styles.vencidoBloque}>
            <p className={styles.infoLinea}>
              <span className={styles.alertMessage}>
                Este presupuesto venció el {formatearFecha(fechaValidez)}
                {idPublicacionEstado === ESTADO_PUBLICADA
                  ? '. Podés pedirle uno nuevo al vendedor.'
                  : ' y la publicación ya no está vigente.'}
              </span>
            </p>
            {idPublicacionEstado === ESTADO_PUBLICADA && (
              <LinkButton to={`/Publicacion/${idPublicacion}`} className={styles.pedirDeNuevoBtn}>
                Pedir presupuesto nuevamente
              </LinkButton>
            )}
          </div>
        )}
      </div>
    </DetalleLayout>
  )
}

PresupuestoCabecera.propTypes = {
  presupuesto: PropTypes.shape({
    idPresupuesto: PropTypes.number,
    idEstado: PropTypes.number,
    estado: PropTypes.string,
    titulo: PropTypes.string,
    idPublicacion: PropTypes.number,
    fechaValidez: PropTypes.string,
    descripcion: PropTypes.string,
    cantDiasEntrega: PropTypes.number,
    anticipo: PropTypes.number,
    empidVendedor: PropTypes.string,
    idPublicacionEstado: PropTypes.number
  }).isRequired,
  estado: PropTypes.number,
  estadoTxt: PropTypes.string,
  esVendedor: PropTypes.bool,
  abrirAdjunto: PropTypes.func.isRequired,
  nombreAdjunto: PropTypes.string,
  mediosPagoBase: PropTypes.arrayOf(
    PropTypes.shape({
      idMedioPago: PropTypes.number.isRequired,
      nombre: PropTypes.string.isRequired,
      icono: PropTypes.string,
      tienePresupuesto: PropTypes.bool.isRequired,
      habilitado: PropTypes.bool.isRequired,
      tooltip: PropTypes.string
    })
  ).isRequired,
  avisoLimiteCompra: PropTypes.string
}

export default PresupuestoCabecera
